import { useState, useEffect, type ChangeEvent } from 'react';

import styles from './TextCloudEditor.module.css';

/**
 * TextCloudEditor Component
 *
 * Specialized editor for text-cloud page type (e.g., About Me page)
 * Allows editing of two text sections: personal info and professional info
 *
 * Features:
 * - Two text areas for personal and professional content
 * - Character count display
 * - Save/Cancel functionality
 * - Unsaved changes tracking
 * - Form validation
 */

/**
 *
 */
type TextCloudEditorProps = {
  readonly slug: string;
  readonly pageTitle: string;
  readonly initialContent: Record<string, unknown>;
  readonly onSave: (content: Record<string, unknown>) => Promise<void>;
  readonly isSaving: boolean;
};

const TextCloudEditor = ({
  pageTitle,
  initialContent,
  onSave,
  isSaving,
}: TextCloudEditorProps): JSX.Element => {
  const [personalInfo, setPersonalInfo] = useState('');
  const [professionalInfo, setProfessionalInfo] = useState('');
  const [personalTitle, setPersonalTitle] = useState('');
  const [professionalTitle, setProfessionalTitle] = useState('');
  const [isDirty, setIsDirty] = useState(false);

  const MAX_LENGTH = 5000; // Maximum characters per field
  const MAX_TITLE_LENGTH = 50; // Maximum characters for titles

  // Initialize form with initial content
  useEffect(() => {
    setPersonalInfo((initialContent.personalInfo as string) || '');
    setProfessionalInfo((initialContent.professionalInfo as string) || '');
    setPersonalTitle((initialContent.personalTitle as string) || 'Personal');
    setProfessionalTitle((initialContent.professionalTitle as string) || 'Professional');
    setIsDirty(false);
  }, [initialContent]);

  // Handle personal info change
  const handlePersonalChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
    setPersonalInfo(e.target.value);
    setIsDirty(true);
  };

  // Handle professional info change
  const handleProfessionalChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
    setProfessionalInfo(e.target.value);
    setIsDirty(true);
  };

  // Handle personal title change
  const handlePersonalTitleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setPersonalTitle(e.target.value);
    setIsDirty(true);
  };

  // Handle professional title change
  const handleProfessionalTitleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setProfessionalTitle(e.target.value);
    setIsDirty(true);
  };

  // Handle save
  const handleSave = async (): Promise<void> => {
    // Validate that at least one field has content
    const trimmedPersonal = personalInfo.trim();
    const trimmedProfessional = professionalInfo.trim();

    if (!trimmedPersonal && !trimmedProfessional) {
      alert('Please enter content in at least one field');
      return;
    }

    try {
      await onSave({
        personalInfo: trimmedPersonal,
        professionalInfo: trimmedProfessional,
        personalTitle: personalTitle.trim() || 'Personal',
        professionalTitle: professionalTitle.trim() || 'Professional',
      });
      setIsDirty(false);
    } catch (err) {
      // Error handled by parent component
      console.error('Save failed:', err);
    }
  };

  // Handle cancel
  const handleCancel = (): void => {
    if (isDirty) {
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to discard them?'
      );
      if (!confirmed) return;
    }

    // Reset to initial values
    setPersonalInfo((initialContent.personalInfo as string) || '');
    setProfessionalInfo((initialContent.professionalInfo as string) || '');
    setPersonalTitle((initialContent.personalTitle as string) || 'Personal');
    setProfessionalTitle((initialContent.professionalTitle as string) || 'Professional');
    setIsDirty(false);
  };

  return (
    <div className={styles.textCloudEditor}>
      {/* Header */}
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Edit: {pageTitle}</h1>
          <p className={styles.subtitle}>Text Cloud Content</p>
        </div>
      </div>

      {/* Form */}
      <div className={styles.form}>
        {/* Personal Information Section */}
        <div className={styles.formSection}>
          <label className={styles.label} htmlFor="personalTitle">
            Title
            <span className={styles.hint}>(top cloud)</span>
          </label>
          <input
            id="personalTitle"
            type="text"
            className={styles.input}
            value={personalTitle}
            onChange={handlePersonalTitleChange}
            placeholder="e.g., Personal, About Me, Who I Am..."
            maxLength={MAX_TITLE_LENGTH}
          />
          <div className={styles.charCount}>
            {personalTitle.length} / {MAX_TITLE_LENGTH} characters
          </div>

          <label className={styles.label} htmlFor="personalInfo" style={{ marginTop: '1rem' }}>
            Personal Information Content
          </label>
          <textarea
            id="personalInfo"
            className={styles.textarea}
            value={personalInfo}
            onChange={handlePersonalChange}
            placeholder="Enter personal information (interests, hobbies, background, etc.)..."
            rows={8}
            maxLength={MAX_LENGTH}
          />
          <div className={styles.charCount}>
            {personalInfo.length} / {MAX_LENGTH} characters
            {personalInfo.length >= MAX_LENGTH && (
              <span className={styles.maxReached}> (Maximum reached)</span>
            )}
          </div>
        </div>

        {/* Professional Experience Section */}
        <div className={styles.formSection}>
          <label className={styles.label} htmlFor="professionalTitle">
            Title
            <span className={styles.hint}>(bottom cloud)</span>
          </label>
          <input
            id="professionalTitle"
            type="text"
            className={styles.input}
            value={professionalTitle}
            onChange={handleProfessionalTitleChange}
            placeholder="e.g., Professional, Experience, What I Do..."
            maxLength={MAX_TITLE_LENGTH}
          />
          <div className={styles.charCount}>
            {professionalTitle.length} / {MAX_TITLE_LENGTH} characters
          </div>

          <label className={styles.label} htmlFor="professionalInfo" style={{ marginTop: '1rem' }}>
            Professional Experience Content
          </label>
          <textarea
            id="professionalInfo"
            className={styles.textarea}
            value={professionalInfo}
            onChange={handleProfessionalChange}
            placeholder="Enter professional experience (work history, skills, expertise, etc.)..."
            rows={8}
            maxLength={MAX_LENGTH}
          />
          <div className={styles.charCount}>
            {professionalInfo.length} / {MAX_LENGTH} characters
            {professionalInfo.length >= MAX_LENGTH && (
              <span className={styles.maxReached}> (Maximum reached)</span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className={styles.actions}>
          <button
            onClick={handleCancel}
            className={styles.cancelButton}
            disabled={isSaving}
            type="button"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className={styles.saveButton}
            disabled={isSaving || !isDirty}
            type="button"
          >
            {isSaving ? (
              <>
                <span className={styles.spinner}></span>
                Saving...
              </>
            ) : (
              '💾 Save Changes'
            )}
          </button>
        </div>

        {/* Unsaved Changes Warning */}
        {isDirty && !isSaving ? (
          <div className={styles.unsavedWarning}>⚠️ You have unsaved changes</div>
        ) : null}
      </div>
    </div>
  );
};

export default TextCloudEditor;
